/*
 *
 * WeekplanCalendar
 *
 */

import { grey } from '@mui/material/colors';
import Toolbar from '@mui/material/Toolbar';
import moment from 'moment';
import { useMutation } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';

import Calendar from '~/components/Calendar';
import CalendarControls from '~/components/CalendarControls';
import { PRIVATE } from '~/components/ShiftDetailCardApplicationStatus/constants';
import WeekplanShiftEvent from '~/components/WeekplanShiftEvent';
import { useCalendarDisplayType } from '~/components/WithCalendarDisplayType';
import CreateShiftButton from '~/components/CreateShiftButton';
import { ShiftResource, shiftsPost } from '~/generated';
import { useProjectCalendarEvents } from '~/hooks/api/useProjectCalendarEvents';
import { extractData } from '~/utils/query-utils';
import styles from './styles.module.css';
import messages from './messages';
import { getRouteApi, useNavigate } from '@tanstack/react-router';

const route = getRouteApi('/weekplan/$year/$week/$day/{-$shiftId}');

type WeekplanCalendarProps = {
  shiftsData: {
    isFetching: boolean;
    shifts: ShiftResource[];
  };
};

export const WeekplanCalendar = (props: WeekplanCalendarProps) => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const params = route.useParams();
  const calendarDisplayType = useCalendarDisplayType();
  const year = parseInt(params.year, 10);
  const week = parseInt(params.week, 10);
  const day = parseInt(params.day, 10);
  const displayedDate = moment().isoWeekYear(year).isoWeek(week).weekday(day);
  const {
    isLoading: isLoadingProjectCalendarEvents,
    data: projectCalendarEvents,
  } = useProjectCalendarEvents(year, week);
  const mutation = useMutation({
    mutationKey: ['shifts', 'create'],
    mutationFn: (values: { startDate: string; endDate: string }) =>
      extractData(shiftsPost({ body: values, responseType: 'json' })),
    onSuccess() {
      showSnackbar({
        message: intl.formatMessage(messages.shiftCreationSucceeded),
      });
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.shiftCreationFailed),
      });
    },
  });
  const handleCalendarSelect = ({
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  }) => mutation.mutate({ startDate, endDate });

  const navigateToDay = (date: Date) => {
    const momentDate = moment(date);
    navigate({
      to: '/weekplan/$year/$week/$day/{-$shiftId}',
      params: {
        year: momentDate.isoWeekYear().toString(),
        week: momentDate.isoWeek().toString(),
        day: momentDate.weekday().toString(),
      },
    });
  };
  const mapShiftsToEvents = (shifts: ShiftResource[]) =>
    shifts
      .filter((shift) => shift && shift.id && shift.startDate && shift.endDate)
      .map((shift) => ({
        id: shift.id!,
        title: '',
        start: shift.startDate,
        end: shift.endDate,
        editable: shift.applicationState === PRIVATE,
      }));
  const redirectToShiftDetailView = (event: { id: number; start: string }) => {
    if (!event.id) return;
    navigate({
      to: '/weekplan/$year/$week/$day/{-$shiftId}',
      params: {
        year: params.year,
        week: params.week,
        day: moment(event.start).weekday().toString(),
        shiftId: event.id.toString(),
      },
    });
  };
  const renderCalendarEvent = (calEvent: { id: number; start: string }) => {
    const shiftForEvent = props.shiftsData.shifts.find(
      (shift) => shift && shift.id === calEvent.id
    );
    if (!shiftForEvent || !shiftForEvent.startDate || !shiftForEvent.endDate) {
      return null;
    }
    return (
      <WeekplanShiftEvent
        onClick={() => redirectToShiftDetailView(calEvent)}
        shift={shiftForEvent}
        isDimmed={
          params.shiftId ? calEvent.id !== parseInt(params.shiftId, 10) : false
        }
        isHighlighted={
          params.shiftId ? calEvent.id === parseInt(params.shiftId, 10) : false
        }
      />
    );
  };
  return (
    <div className={styles.container}>
      {calendarDisplayType === 'day' ? (
        <Toolbar
          className={styles.toolbar}
          style={{
            backgroundColor: grey[300],
            justifyContent: 'center',
          }}
        >
          <CalendarControls
            date={displayedDate}
            onChange={navigateToDay}
            displayType={calendarDisplayType}
          />
        </Toolbar>
      ) : null}
      <div className={styles.calendar}>
        {!props.shiftsData.isFetching && !isLoadingProjectCalendarEvents ? (
          <Calendar
            date={displayedDate}
            events={mapShiftsToEvents(props.shiftsData.shifts)}
            displayType={calendarDisplayType}
            onCreateShift={handleCalendarSelect}
            onEventRender={renderCalendarEvent}
            projectCalendarEvents={projectCalendarEvents?.events}
          />
        ) : null}
        <CreateShiftButton date={displayedDate.toISOString()} />
      </div>
    </div>
  );
};

export default WeekplanCalendar;
